import {
  netFromTax,
  splitNetByTax,
  taxFromNet,
  type SplitLine,
  type VatRate,
} from "@devengo/shared";

import { centsDecimal, decimalCents, formatCents, parseUserAmount } from "./format";
import type { VoucherView } from "./hooks";

/**
 * The amounts of the previous system's purchase screen (plan 016): one neto gravado, the IVA of
 * each rate, no gravado, exento, perceptions. Devengo stores a neto per rate, so the neto is split
 * from each rate's IVA with the same function the import uses (`splitNetByTax`).
 */
export interface FixedAmounts {
  neto: string;
  noGravado: string;
  exento: string;
  iva105: string;
  iva21: string;
  iva27: string;
  percIva: string;
  percIb: string;
  /** Convenio Multilateral code as text ("902"). */
  jurisdiction: string;
}

export const FIXED_RATES = [
  { rate: "10.5", key: "iva105", label: "IVA 10,5%" },
  { rate: "21", key: "iva21", label: "IVA 21%" },
  { rate: "27", key: "iva27", label: "IVA 27%" },
] as const satisfies readonly { rate: VatRate; key: keyof FixedAmounts; label: string }[];

export const EMPTY_AMOUNTS: FixedAmounts = {
  neto: "",
  noGravado: "",
  exento: "",
  iva105: "",
  iva21: "",
  iva27: "",
  percIva: "",
  percIb: "",
  jurisdiction: "902",
};

export interface LinePayload {
  kind: "gravado" | "exento" | "no_gravado" | "percepcion_iva" | "percepcion_iibb";
  rate?: VatRate;
  net: string;
  tax: string;
  jurisdiction?: number;
}

export interface FixedResult {
  lines: LinePayload[];
  /** Amounts that could not be read, or a neto the IVA cannot explain. */
  problems: string[];
  /** The neto per rate that will be stored, when it was split across more than one rate. */
  split: SplitLine[] | null;
  sumCents: number;
}

const LABELS: Record<keyof FixedAmounts, string> = {
  neto: "Neto gravado",
  noGravado: "No gravado",
  exento: "Exento",
  iva105: "IVA 10,5%",
  iva21: "IVA 21%",
  iva27: "IVA 27%",
  percIva: "Percepción IVA",
  percIb: "Percepción IB",
  jurisdiction: "Jurisdicción",
};

/**
 * The voucher lines of the fixed screen. `noIva`: a B or C purchase, which discriminates no IVA —
 * its neto is no gravado, as the import does with the previous system's B and C purchases.
 */
export function fixedLines(amounts: FixedAmounts, noIva: boolean): FixedResult {
  const problems: string[] = [];
  const cents = (key: keyof FixedAmounts): number => {
    const parsed = parseUserAmount(amounts[key]);
    if (parsed === null) {
      problems.push(
        `No se entiende el importe "${amounts[key]}" en ${LABELS[key]}. Usá el formato 1.234,56.`,
      );
      return 0;
    }
    return decimalCents(parsed);
  };
  const neto = cents("neto");
  const taxes = FIXED_RATES.map(({ rate, key }) => ({ rate, taxCents: noIva ? 0 : cents(key) }));
  const noGravado = cents("noGravado");
  const exento = cents("exento");
  const percIva = cents("percIva");
  const percIb = cents("percIb");
  const lines: LinePayload[] = [];
  let split: SplitLine[] | null = null;

  const hasTax = taxes.some((t) => t.taxCents !== 0);
  let extraNoGravado = 0;
  if (noIva) {
    extraNoGravado = neto;
  } else if (neto !== 0 || hasTax) {
    if (neto === 0) {
      problems.push("Hay IVA cargado pero el neto gravado está vacío.");
    } else {
      const result = splitNetByTax(neto, taxes);
      if (!result.ok) {
        problems.push(result.reason.charAt(0).toUpperCase() + result.reason.slice(1));
      } else {
        for (const line of result.lines) {
          lines.push({
            kind: "gravado",
            rate: line.rate,
            net: centsDecimal(line.netCents),
            tax: centsDecimal(line.taxCents),
          });
        }
        if (result.derived) split = result.lines;
      }
    }
  }
  if (exento) lines.push({ kind: "exento", net: centsDecimal(exento), tax: "0.00" });
  if (noGravado + extraNoGravado) {
    lines.push({ kind: "no_gravado", net: centsDecimal(noGravado + extraNoGravado), tax: "0.00" });
  }
  if (percIva) lines.push({ kind: "percepcion_iva", net: "0.00", tax: centsDecimal(percIva) });
  if (percIb) {
    lines.push({
      kind: "percepcion_iibb",
      net: "0.00",
      tax: centsDecimal(percIb),
      // Empty: a stored perception without province stays without one.
      ...(amounts.jurisdiction ? { jurisdiction: Number(amounts.jurisdiction) } : {}),
    });
  }
  const sumCents =
    neto + noGravado + exento + percIva + percIb + taxes.reduce((a, t) => a + t.taxCents, 0);
  return { lines, problems, split, sumCents };
}

/**
 * The IVA of one rate for the → shortcut: on the neto that the other rates' IVA does not already
 * account for, so a mixed invoice is typed as on paper — IVA 10,5% first, then → on 21%.
 */
export function suggestedTax(amounts: FixedAmounts, rate: VatRate): string | null {
  const neto = parseUserAmount(amounts.neto);
  if (neto === null || decimalCents(neto) <= 0) return null;
  let rest = decimalCents(neto);
  for (const other of FIXED_RATES) {
    if (other.rate === rate) continue;
    const tax = parseUserAmount(amounts[other.key]);
    if (tax === null) return null;
    rest -= netFromTax(decimalCents(tax), other.rate);
  }
  if (rest <= 0) return null;
  return centsDecimal(taxFromNet(rest, rate));
}

const toInput = (cents: number) => (cents === 0 ? "" : formatCents(cents));

/**
 * A stored voucher in the fixed screen's terms, or null when the screen cannot show it as it is
 * (rates 0 / 2,5 / 5, other taxes, a repeated concept, foreign currency, or netos the IVA split
 * would not reproduce): those are edited line by line, so saving never rewrites them silently.
 */
export function fixedFromView(view: VoucherView): FixedAmounts | null {
  if (view.currency !== "ARS") return null;
  const amounts: FixedAmounts = { ...EMPTY_AMOUNTS };
  const seen = new Set<string>();
  let neto = 0;
  const taxes: { rate: VatRate; taxCents: number }[] = [];
  const nets: number[] = [];
  for (const line of view.lines) {
    const key = line.kind === "gravado" ? `gravado:${line.rate}` : line.kind;
    if (seen.has(key)) return null;
    seen.add(key);
    const net = decimalCents(line.net);
    const tax = decimalCents(line.tax);
    switch (line.kind) {
      case "gravado": {
        const fixed = FIXED_RATES.find((r) => r.rate === line.rate);
        if (!fixed || tax === 0) return null;
        amounts[fixed.key] = toInput(tax);
        neto += net;
        taxes.push({ rate: fixed.rate, taxCents: tax });
        nets.push(net);
        break;
      }
      case "exento":
        amounts.exento = toInput(net);
        break;
      case "no_gravado":
        amounts.noGravado = toInput(net);
        break;
      case "percepcion_iva":
        amounts.percIva = toInput(tax);
        break;
      case "percepcion_iibb":
        amounts.percIb = toInput(tax);
        amounts.jurisdiction = line.jurisdiction === null ? "" : String(line.jurisdiction);
        break;
      default:
        return null;
    }
  }
  amounts.neto = toInput(neto);
  // What saving would store, rate by rate, must be what is stored (Codex 016 M2: a cent can move
  // between rates when the stored netos were typed line by line).
  const again = fixedLines(amounts, false);
  if (again.problems.length) return null;
  const savedBack = again.lines.filter((l) => l.kind === "gravado");
  if (savedBack.length !== taxes.length) return null;
  for (const line of savedBack) {
    const index = taxes.findIndex((t) => t.rate === line.rate);
    if (index === -1 || decimalCents(line.net) !== nets[index]) return null;
    if (decimalCents(line.tax) !== taxes[index]?.taxCents) return null;
  }
  return amounts;
}
